import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authService } from '../services/authService';
import axiosInstance from '../utils/axiosConfig';

// Create the context with a more complete initial state
export const AuthContext = createContext({
    user: null,
    isAuthenticated: false,
    login: () => {},
    logout: () => {},
    updateUser: () => {}
});

// Create the provider component
export const AuthProvider = ({ children }) => {
    const [authState, setAuthState] = useState({
        user: null,
        isAuthenticated: false
    });

    useEffect(() => {
        // With cookie-based auth, we just need to check if isLoggedIn flag is set
        const isLoggedIn = localStorage.getItem('isLoggedIn') === 'true';
        const userId = localStorage.getItem('user_id');
        
        if (isLoggedIn && userId) {
            // Set initial minimal state
            setAuthState({
                user: {
                    id: parseInt(userId)
                },
                isAuthenticated: true
            });

            // Pre-warm the GPU AI endpoints for a returning session. Fresh logins
            // are warmed server-side; this covers users who reopen the app with a
            // still-valid session and never re-POST credentials. Fully guarded and
            // debounced server-side (only warms a cold endpoint); fire-and-forget.
            axiosInstance.post('/api/knowledge-hub/warm-llm/').catch(() => {});

            // Fetch full user info from API
            const fetchUserInfo = async () => {
                try {
                    const response = await axiosInstance.get('/api/authentication/user/');
                    if (response.data) {
                        setAuthState(prev => ({
                            ...prev,
                            user: {
                                ...prev.user,
                                ...response.data
                            }
                        }));
                    }
                } catch (error) {
                    console.error('Error fetching user info on refresh:', error);
                    // If API call fails (e.g., session expired), don't clear state
                    // Let the axios interceptor handle 401 errors
                }
            };
            
            fetchUserInfo();
        }
    }, []);

    const login = useCallback((userData) => {
        // Store minimal user info - no more tokens in localStorage
        localStorage.setItem('user_id', userData.id.toString());
        localStorage.setItem('isLoggedIn', 'true');

        setAuthState({
            user: userData,
            isAuthenticated: true
        });
    }, []);

    const logout = useCallback(async () => {
        try {
            // Use the auth service to logout which will clear cookies server-side
            await authService.logout();
        } catch (error) {
            console.error("Logout error:", error);
        } finally {
            // Clear local storage regardless of server-side success
            localStorage.removeItem('user_id');
            localStorage.removeItem('isLoggedIn');
            
            // Reset auth state
            setAuthState({
                user: null,
                isAuthenticated: false
            });
        }
    }, []);

    const updateUser = useCallback((userData) => {
        setAuthState(prev => ({
            ...prev,
            user: { ...prev.user, ...userData }
        }));
    }, []);

    return (
        <AuthContext.Provider 
            value={{
                ...authState,
                login,
                logout,
                updateUser
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};

// Custom hook for using the auth context
export const useAuth = () => useContext(AuthContext);
